/**
 * 小さな RGB 画像を PNG の data URL にする（切盛分布図のように、マスを 1 画素で描くための道具）。
 * 圧縮はしない（deflate の stored ブロック）。外部ライブラリ・DOM・Node の API を使わない。
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array, start = 0, end = bytes.length): number {
  let c = 0xffffffff;
  for (let i = start; i < end; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function adler32(bytes: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (const x of bytes) {
    a = (a + x) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

/** zlib 形式（無圧縮）。 */
function zlibStored(data: Uint8Array): Uint8Array {
  const blocks = Math.max(1, Math.ceil(data.length / 65535));
  const out = new Uint8Array(2 + data.length + blocks * 5 + 4);
  let o = 0;
  out[o++] = 0x78;
  out[o++] = 0x01;
  for (let b = 0; b < blocks; b++) {
    const start = b * 65535;
    const len = Math.min(65535, data.length - start);
    out[o++] = b === blocks - 1 ? 1 : 0;
    out[o++] = len & 0xff;
    out[o++] = len >>> 8;
    out[o++] = ~len & 0xff;
    out[o++] = (~len >>> 8) & 0xff;
    out.set(data.subarray(start, start + len), o);
    o += len;
  }
  const ad = adler32(data);
  out[o++] = ad >>> 24;
  out[o++] = (ad >>> 16) & 0xff;
  out[o++] = (ad >>> 8) & 0xff;
  out[o] = ad & 0xff;
  return out;
}

const BASE64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
export function base64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    s += BASE64[n >>> 18]! + BASE64[(n >>> 12) & 63]!;
    s += b === undefined ? '=' : BASE64[(n >>> 6) & 63]!;
    s += c === undefined ? '=' : BASE64[n & 63]!;
  }
  return s;
}

/**
 * 幅 w・高さ h の RGB 画像（rgb は行ごとに左から [r,g,b] を並べた 0〜255 の値）を PNG のバイト列にする。
 */
export function encodePng(w: number, h: number, rgb: Uint8Array): Uint8Array {
  if (!(Number.isInteger(w) && Number.isInteger(h) && w > 0 && h > 0) || rgb.length !== w * h * 3)
    throw new RangeError('PNG の大きさと画素の数が合わない');
  const raw = new Uint8Array(h * (w * 3 + 1));
  for (let y = 0; y < h; y++) {
    raw[y * (w * 3 + 1)] = 0; // フィルタなし
    raw.set(rgb.subarray(y * w * 3, (y + 1) * w * 3), y * (w * 3 + 1) + 1);
  }
  const idat = zlibStored(raw);
  const chunks: [string, Uint8Array][] = [
    [
      'IHDR',
      new Uint8Array([
        w >>> 24,
        (w >>> 16) & 255,
        (w >>> 8) & 255,
        w & 255,
        h >>> 24,
        (h >>> 16) & 255,
        (h >>> 8) & 255,
        h & 255,
        8,
        2,
        0,
        0,
        0,
      ]),
    ],
    ['IDAT', idat],
    ['IEND', new Uint8Array(0)],
  ];
  const size = 8 + chunks.reduce((n, [, d]) => n + 12 + d.length, 0);
  const out = new Uint8Array(size);
  out.set([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], 0);
  let o = 8;
  for (const [type, data] of chunks) {
    const len = data.length;
    out.set([len >>> 24, (len >>> 16) & 255, (len >>> 8) & 255, len & 255], o);
    for (let i = 0; i < 4; i++) out[o + 4 + i] = type.charCodeAt(i);
    out.set(data, o + 8);
    const c = crc32(out, o + 4, o + 8 + len);
    out.set([c >>> 24, (c >>> 16) & 255, (c >>> 8) & 255, c & 255], o + 8 + len);
    o += 12 + len;
  }
  return out;
}

export const pngDataUrl = (w: number, h: number, rgb: Uint8Array): string =>
  `data:image/png;base64,${base64(encodePng(w, h, rgb))}`;
